<template>
  <div
    class="ai-chat-container"
    :class="{ 'is-expanded': isExpanded }"
    :style="containerStyle"
    @touchstart="handleTouchStart"
    @touchmove="handleTouchMove"
    @touchend="handleTouchEnd"
    @mousedown="handleTouchStart"
    @mousemove="handleTouchMove"
    @mouseup="handleTouchEnd"
    @mouseleave="handleTouchEnd"
  >
    <!-- Collapsed Bar -->
    <div v-if="false && !isExpanded" class="ai-chat-bar">
      <div class="ai-icon-wrapper">
        <font-awesome-icon :icon="['fas', 'lightbulb']" class="ai-icon" />
      </div>
      <div class="ai-input-placeholder">
        <transition name="slide-fade" mode="out-in">
          <div :key="currentPlaceholderIndex" class="placeholder-item">
            {{ placeholderList[currentPlaceholderIndex] }}
          </div>
        </transition>
      </div>
    </div>

    <!-- Expanded Chat Window -->
    <van-popup
      v-model="isExpanded"
      position="bottom"
      :style="{ height: '100%', width: '100%' }"
      class="ai-chat-popup"
      get-container="body"
    >
      <div class="chat-window">
        <div class="chat-header">
          <div class="title-left">
            <font-awesome-icon :icon="['fas', 'lightbulb']" class="header-icon" />
            <span class="title">{{ $t('ai_assistant') }}</span>
          </div>
          <div class="header-actions">
            <van-icon v-if="currentChatDbId" name="plus" class="history-icon" @click="startNewChat" />
            <van-icon name="clock-o" class="history-icon" @click="toggleHistoryList" />
            <van-icon name="cross" class="close-icon" @click="collapseChat" />
          </div>
        </div>

        <!-- History List Overlay -->
        <div v-if="showHistoryList" class="history-overlay">
          <div class="history-header">
            <span>{{ $t('history_title') }}</span>
            <van-icon name="cross" @click="showHistoryList = false" />
          </div>
          <div ref="historyList" class="history-content" @scroll="onHistoryScroll">
            <div
              v-for="session in sessionList"
              :key="session.user_copilot_chat_id"
              class="history-item"
              @click="selectSession(session)"
            >
              <div class="history-info">
                <div class="history-title">{{ session.chat_title || '未命名会话' }}</div>
                <div class="history-time">{{ formatTime(session.chat_time) }}</div>
              </div>
              <van-icon name="delete" class="delete-icon" @click.stop="handleDeleteSession(session)" />
            </div>
            <div v-if="sessionLoading" class="loading-more">
              <span class="dot-loading">
                <span class="dot"></span>
                <span class="dot"></span>
                <span class="dot"></span>
              </span>
            </div>
            <div v-if="sessionList.length > 0 && sessionFinished" class="no-more">{{ $t('no_more_txt') }}</div>
            <div v-if="sessionList.length === 0 && !sessionLoading" class="no-history">{{ $t('no_history') }}</div>
          </div>
        </div>

        <div
          v-show="!showHistoryList"
          ref="chatBody"
          class="chat-body"
          :class="{ 'is-empty': chatHistory.length === 0 && !historyLoading }"
        >
          <div v-if="historyLoading" class="history-loading">
            <div class="typing-indicator">
              <span class="dot"></span>
              <span class="dot"></span>
              <span class="dot"></span>
            </div>
          </div>

          <div v-else-if="chatHistory.length === 0" class="empty-state">
            <div class="welcome-text">
              <p style="margin-bottom: 25px;">{{ $t('ai_intro') }}</p>

              <div class="section-title">{{ $t('quick_workflow') }}</div>
              <div class="workflow-guide">
                <div class="guide-title">{{ $t('workflow_example') }}</div>
                <span v-html="$t('workflow_guide_content')"></span>
              </div>
            </div>

            <div class="section-title" style="margin-top: 20px;">{{ $t('suggestions_and_inquiries') }}</div>
            <div class="quick-actions">
              <div
                v-for="(action, index) in quickActions"
                :key="index"
                class="action-chip"
                @click="handleQuickAction(action)"
              >
                {{ action }}
              </div>
            </div>
          </div>

          <div v-else class="message-list">
            <div v-for="(msg, index) in chatHistory" :key="index" class="message-item" :class="msg.type">
              <div class="bubble">
                <div
                  v-if="msg.type === 'ai' && msg.thoughtSeconds"
                  class="thought-time"
                  :class="{ 'is-clickable': hasThoughtText(msg), 'is-open': isThoughtMessageExpanded(index) }"
                  @click="hasThoughtText(msg) && toggleThoughtMessage(index)"
                >
                  {{ $t('ai_thought_seconds', { seconds: msg.thoughtSeconds }) }}
                </div>
                <div v-if="msg.loading && !msg.text" class="typing-indicator">
                  <span
                    class="thinking-text"
                    :class="{ 'is-clickable': hasThoughtText(msg), 'is-open': isThoughtMessageExpanded(index) }"
                    @click.stop="hasThoughtText(msg) && toggleThoughtMessage(index)"
                  >
                    {{ currentThinkingText }}
                  </span>
                  <span class="dot"></span>
                  <span class="dot"></span>
                  <span class="dot"></span>
                </div>
                <div
                  v-if="hasThoughtText(msg) && isThoughtMessageExpanded(index)"
                  class="thought-content"
                  v-html="formatMessage(msg.thoughtText)"
                ></div>
                <div
                  class="message-text"
                  :class="{ 'is-clamped': isUserMessageCollapsed(msg, index) }"
                  v-html="formatMessage(msg.text)"
                ></div>
                <button
                  v-if="shouldClampUserMessage(msg)"
                  type="button"
                  class="message-toggle"
                  @click="toggleUserMessage(index)"
                >
                  {{ getUserMessageToggleText(index) }}
                </button>
                <div v-if="msg.references && msg.references.length > 0" class="references-list">
                  <div class="ref-header">引用来源:</div>
                  <div v-for="ref in msg.references" :key="ref.index_id" class="ref-item">
                    <span class="ref-index">[{{ ref.index_id }}]</span>
                    <span class="ref-content">
                      <span class="ref-title">{{ ref.title || ref.doc_name }}</span>
                    </span>
                  </div>
                </div>
              </div>
            </div>
          </div>
        </div>

        <div class="chat-footer">
          <div class="input-row" :class="{ 'focused': isFocused }">
            <div class="input-wrapper">
              <van-field
                v-model="question"
                type="textarea"
                autosize
                rows="1"
                :border="false"
                class="text-input"
                :placeholder="$t('input_placeholder')"
                @focus="isFocused = true"
                @blur="isFocused = false"
                @keyup.enter="handleAsk"
              />
            </div>
            <div class="thinking-dropdown" @click.stop>
              <button
                type="button"
                class="thinking-mode-trigger"
                :class="{ open: showThinkingModeMenu }"
                :disabled="isLoading"
                @click="toggleThinkingModeMenu"
              >
                <span>{{ thinkingModeLabel }}</span>
                <van-icon name="arrow-down" class="thinking-mode-arrow" />
              </button>
              <div v-if="showThinkingModeMenu" class="thinking-mode-menu">
                <button
                  v-for="option in thinkingModeOptions"
                  :key="option.value"
                  type="button"
                  class="thinking-mode-option"
                  :class="{ active: option.value === thinkingEnabled }"
                  @click="selectThinkingMode(option.value)"
                >
                  <span>{{ option.label }}</span>
                  <van-icon v-if="option.value === thinkingEnabled" name="success" />
                </button>
              </div>
            </div>
            <div class="action-btn">
              <van-icon
                v-if="question"
                name="guide-o"
                size="28"
                color="#00b852"
                style="padding: 10px;"
                @click="handleAsk"
              />
              <SpeechToText
                v-else
                class="speech-wrapper"
                @translateResult="handleVoiceResult"
              >
                <template #trigger>
                  <div class="mic-btn-integrated">
                    <font-awesome-icon :icon="['fas', 'microphone']" style="color: #fff; font-size: 24px;" />
                  </div>
                </template>
              </SpeechToText>
            </div>
          </div>
        </div>
      </div>
    </van-popup>
  </div>
</template>

<script>
import { Popup, Icon, Button, Toast, Field, Dialog } from 'vant';
import { wecomSDK } from '@/utils/wecom';
import { streamSuperRepCopilot, saveChatHistory, getChatHistory, saveChatSession, getChatSessions, deleteChatSession } from '@/api/ai';
import { getLocalStorage, setLocalStorage, formatMessage, isSalesManagerTerritory } from '@/utils/index';
import SpeechToText from '@/components/SpeechToText.vue';

export default {
  name: 'AIChatBar',
  components: {
    [Popup.name]: Popup,
    [Icon.name]: Icon,
    [Button.name]: Button,
    [Field.name]: Field,
    [Dialog.Component.name]: Dialog.Component,
    SpeechToText
  },
  data() {
    const elWidth = 130 + 20;
    const elHeight = 66 + 10;
    const screenWidth = typeof window !== 'undefined' ? window.innerWidth : 375;
    const screenHeight = typeof window !== 'undefined' ? window.innerHeight : 667;

    return {
      isExpanded: false,
      question: '',
      isFocused: false,
      isRecording: false,
      chatHistory: [],
      sessionId: null,
      isLoading: false,
      // Dragging state
      dragging: false,
      startX: 0,
      startY: 0,
      left: screenWidth - elWidth - 6,
      top: screenHeight - 60 - elHeight,
      screenWidth: screenWidth,
      screenHeight: screenHeight,
      elWidth: elWidth,
      elHeight: elHeight, // Approximate height of the bar
      isClick: false,
      currentPlaceholderIndex: 0,
      placeholderTimer: null,
      thinkingMessageIndex: 0,
      thinkingTimer: null,
      isSessionCreated: false,
      showHistoryList: false,
      sessionList: [],
      currentChatDbId: null,
      sessionPage: 1,
      sessionPageSize: 20,
      sessionFinished: false,
      sessionLoading: false,
      historyLoading: false,
      expandedUserMessages: {},
      expandedThoughtMessages: {},
      currentCopilotAgentName: 'SRCopilot',
      thinkingEnabled: true,
      showThinkingModeMenu: false
    };
  },
  computed: {
    containerStyle() {
      return {
        left: `${this.left}px`,
        top: `${this.top}px`,
        transition: this.dragging ? 'none' : 'all 0.3s cubic-bezier(0.18, 0.89, 0.32, 1.28)'
      };
    },
    quickActions() {
      return [
        this.$t('next_visit_suggestion'),
        this.$t('vyepti_advantage'),
        this.$t('xx_updates'),
        this.$t('visit_prep')
      ];
    },
    placeholderList() {
      return [
        this.$t('placeholder_ai_assistant'),
        this.$t('placeholder_smart_qa'),
        this.$t('placeholder_ai_visit'),
        this.$t('placeholder_ai_suggestion')
      ];
    },
    thinkingMessages() {
      return [
        this.$t('ai_thinking'),
        this.$t('ai_thinking_context'),
        this.$t('ai_thinking_lookup'),
        this.$t('ai_thinking_answer')
      ];
    },
    currentThinkingText() {
      return this.thinkingMessages[this.thinkingMessageIndex % this.thinkingMessages.length];
    },
    thinkingModeOptions() {
      return [
        { value: false, label: this.$t('ai_thinking_default') },
        { value: true, label: this.$t('ai_thinking_toggle') }
      ];
    },
    thinkingModeLabel() {
      return this.thinkingEnabled ? this.$t('ai_thinking_toggle') : this.$t('ai_thinking_default');
    }
  },
  mounted() {
    this.initWecom();
    this.$root.$on('open-ai-chat', this.handleGlobalOpen);
    this.initPosition();
    window.addEventListener('resize', this.handleResize);
    document.addEventListener('click', this.closeThinkingModeMenu);
    this.startPlaceholderAnimation();

    // Mount global click handler for visit links
    window.handleVisitClick = (visitId) => {
      this.collapseChat();
      this.$router.push({
        path: '/hcp/visit-create',
        query: {
          id: visitId,
          mode: 'view'
        }
      });
    };
  },
  beforeDestroy() {
    this.$root.$off('open-ai-chat', this.handleGlobalOpen);
    window.removeEventListener('resize', this.handleResize);
    document.removeEventListener('click', this.closeThinkingModeMenu);
    this.stopPlaceholderAnimation();
    this.stopThinkingAnimation();
    delete window.handleVisitClick;
  },
  methods: {
    initPosition() {
      this.screenWidth = window.innerWidth;
      this.screenHeight = window.innerHeight;
      // Initial position: Bottom Right
      // right: 6px, bottom: 60px
      this.left = this.screenWidth - this.elWidth - 6;
      this.top = this.screenHeight - 60 - this.elHeight;
    },
    handleResize() {
      this.screenWidth = window.innerWidth;
      this.screenHeight = window.innerHeight;
      this.snapToCorner();
    },
    handleTouchStart(e) {
      if (this.isExpanded) return;
      this.dragging = true;
      this.isClick = true;
      const touch = e.touches ? e.touches[0] : e;
      this.startX = touch.clientX - this.left;
      this.startY = touch.clientY - this.top;
    },
    handleTouchMove(e) {
      if (!this.dragging) return;
      e.preventDefault();
      this.isClick = false;
      const touch = e.touches ? e.touches[0] : e;
      this.left = touch.clientX - this.startX;
      this.top = touch.clientY - this.startY;
    },
    handleTouchEnd(e) {
      if (!this.dragging) return;
      this.dragging = false;
      if (this.isClick) {
        // Use setTimeout to avoid conflict with click event that might close the popup immediately
        setTimeout(() => {
          this.expandChat();
        }, 100);
      } else {
        this.snapToCorner();
      }
    },
    snapToCorner() {
      const { left, top, elWidth, elHeight, screenWidth, screenHeight } = this;
      const margin = 6;
      const topMargin = 30;
      const bottomMargin = 60;

      // Corners coordinates (top-left of the element)
      const tl = { x: margin, y: margin + topMargin };
      const tr = { x: screenWidth - elWidth - margin, y: margin + topMargin };
      const bl = { x: margin, y: screenHeight - elHeight - bottomMargin };
      const br = { x: screenWidth - elWidth - margin, y: screenHeight - elHeight - bottomMargin };

      const corners = [tl, tr, bl, br];

      // Find closest corner
      let minDist = Infinity;
      let target = br;

      corners.forEach(c => {
        const dist = Math.pow(c.x - left, 2) + Math.pow(c.y - top, 2);
        if (dist < minDist) {
          minDist = dist;
          target = c;
        }
      });

      this.left = target.x;
      this.top = target.y;
    },
    getDefaultCopilotAgentName() {
      if (this.$route && this.$route.path && this.$route.path.indexOf('/ga-admin') === 0) {
        return 'SRCopilotInsightGBA';
      }
      if (isSalesManagerTerritory()) {
        return 'SRCopilotSalesManager';
      }
      return 'SRCopilot';
    },
    handleGlobalOpen(question, shouldStartNewSession = false, agentName) {
      if (shouldStartNewSession) {
        this.startNewChat();
      }
      this.currentCopilotAgentName = agentName || this.getDefaultCopilotAgentName();
      this.isExpanded = true;
      if (question) {
        this.question = question;
        this.handleAsk();
      }
    },
    startPlaceholderAnimation() {
      this.placeholderTimer = setInterval(() => {
        this.currentPlaceholderIndex = (this.currentPlaceholderIndex + 1) % this.placeholderList.length;
      }, 3000);
    },
    stopPlaceholderAnimation() {
      if (this.placeholderTimer) {
        clearInterval(this.placeholderTimer);
        this.placeholderTimer = null;
      }
    },
    startThinkingAnimation() {
      this.stopThinkingAnimation();
      this.thinkingMessageIndex = 0;
      this.thinkingTimer = setInterval(() => {
        this.thinkingMessageIndex = (this.thinkingMessageIndex + 1) % this.thinkingMessages.length;
      }, 1800);
    },
    stopThinkingAnimation() {
      if (this.thinkingTimer) {
        clearInterval(this.thinkingTimer);
        this.thinkingTimer = null;
      }
    },
    toggleThinkingModeMenu() {
      if (this.isLoading) return;
      this.showThinkingModeMenu = !this.showThinkingModeMenu;
    },
    selectThinkingMode(value) {
      if (this.isLoading) return;
      this.thinkingEnabled = value;
      this.showThinkingModeMenu = false;
    },
    closeThinkingModeMenu() {
      this.showThinkingModeMenu = false;
    },
    toggleHistoryList() {
      this.showHistoryList = !this.showHistoryList;
      if (this.showHistoryList) {
        // Reset list when opening
        this.sessionList = [];
        this.sessionPage = 1;
        this.sessionFinished = false;
        this.loadSessionList();
      }
    },
    async handleDeleteSession(session) {
      Dialog.confirm({
        title: this.$t('prompt_hint'),
        message: this.$t('prompt_delete_session'),
      })
        .then(async () => {
          try {
            await deleteChatSession(session.user_copilot_chat_id);
            Toast.success(this.$t('prompt_delete_success'));
            // Remove from local list to update UI immediately
            this.sessionList = this.sessionList.filter(s => s.user_copilot_chat_id !== session.user_copilot_chat_id);

            // If the deleted session is currently active, clear the chat view
            if (this.currentChatDbId === session.user_copilot_chat_id) {
              this.startNewChat();
            }
          } catch (e) {
            console.error('Delete session failed', e);
            Toast.fail(this.$t('prompt_delete_fail'));
          }
        })
        .catch(() => {
          // on cancel
        });
    },
    startNewChat() {
      if (this.isLoading) return;
      this.currentCopilotAgentName = this.getDefaultCopilotAgentName();
      this.chatHistory = [];
      this.expandedUserMessages = {};
      this.expandedThoughtMessages = {};
      this.sessionId = null;
      this.currentChatDbId = null;
      this.isSessionCreated = false;
      this.showHistoryList = false;
      this.question = '';
      setLocalStorage('ai_copilot_session_id', '');
    },
    async loadSessionList() {
      if (this.sessionLoading || this.sessionFinished) return;

      this.sessionLoading = true;
      try {
        const userId = getLocalStorage('user_id') || this.$store.state.user.userId;
        const roleId = getLocalStorage('role_id') || '';

        const res = await getChatSessions(userId, roleId, this.sessionPage, this.sessionPageSize);
        const list = res.value || [];
        const totalCount = res['@odata.count'] ? Number(res['@odata.count']) : undefined;

        if (this.sessionPage === 1) {
          this.sessionList = list;
        } else {
          this.sessionList = [...this.sessionList, ...list];
        }

        // Use strict count comparison if available
        if (typeof totalCount !== 'undefined') {
          if (this.sessionList.length >= totalCount) {
            this.sessionFinished = true;
          } else {
            this.sessionPage++;
            this.sessionFinished = false;
          }
        } else {
          if (list.length < this.sessionPageSize) {
            this.sessionFinished = true;
          } else {
            this.sessionPage++;
            this.sessionFinished = false;
          }
        }

        // Auto load more if content is too short to scroll
        this.$nextTick(() => {
          const el = this.$refs.historyList;
          if (el && !this.sessionFinished && el.scrollHeight <= el.clientHeight) {
            this.loadSessionList();
          }
        });
      } catch (e) {
        console.error('Fetch sessions failed', e);
        this.sessionFinished = true; // Stop loading on error
      } finally {
        this.sessionLoading = false;
      }
    },
    onHistoryScroll(e) {
      const target = e.target;
      if (target.scrollTop + target.clientHeight >= target.scrollHeight - 20) {
        this.loadSessionList();
      }
    },
    async selectSession(session) {
      this.currentChatDbId = session.user_copilot_chat_id;
      this.isSessionCreated = true;
      this.showHistoryList = false;
      this.chatHistory = []; // clear current view
      this.expandedUserMessages = {};
      this.expandedThoughtMessages = {};
      await this.loadHistory();
    },
    formatTime(isoString) {
      if (!isoString) return '';
      const date = new Date(isoString);
      return date.toLocaleString('zh-CN', { month: '2-digit', day: '2-digit', hour: '2-digit', minute: '2-digit' });
    },
    async initWecom() {
      try {
        await wecomSDK.register();
      } catch (e) {
        console.error('WeCom SDK init failed:', e);
      }
    },
    expandChat() {
      this.isExpanded = true;
    },
    collapseChat() {
      this.isExpanded = false;
    },
    handleVoiceResult(text) {
      if (text) {
        this.question += text;
      }
    },
    handleQuickAction(action) {
      this.question = action;
      // this.handleAsk();
    },
    async loadHistory() {
      if (!this.currentChatDbId) return;
      this.historyLoading = true;
      try {
        const res = await getChatHistory(this.currentChatDbId);
        const list = res.value || [];
        this.chatHistory = [];
        this.expandedUserMessages = {};
        this.expandedThoughtMessages = {};
        if (list.length > 0) {
          // Recover LLM Session ID from history if available
          const lastItem = list[list.length - 1];
          if (lastItem && lastItem.session_id) {
            this.sessionId = lastItem.session_id;
          }

          this.chatHistory = list.map(item => {
            let itemText = item.content;
            let thoughtText = item.thoughts || '';
            let references = [];

            // Try parsing JSON for AI role
            if (item.chat_role === 'ai') {
              try {
                const parsed = JSON.parse(item.content);
                if (parsed && typeof parsed === 'object') {
                  if (parsed.text) itemText = parsed.text;
                  if (parsed.thoughts || parsed.thoughtText) thoughtText = parsed.thoughts || parsed.thoughtText;
                  if (parsed.references) references = parsed.references;
                }
              } catch (e) {
                // Not JSON or plain text
              }
            }

            return {
              type: item.chat_role,
              text: itemText,
              thoughtText: thoughtText,
              references: references,
              loading: false,
              thoughtStartedAt: null,
              thoughtSeconds: item.think_seconds ? Number(item.think_seconds) : null,
              totalStartedAt: null,
              totalSeconds: item.total_seconds ? Number(item.total_seconds) : null
            };
          });

          this.$nextTick(() => {
            const body = this.$refs.chatBody;
            if (body) body.scrollTop = body.scrollHeight;
          });
        }
      } catch (e) {
        console.error('Load history failed', e);
      } finally {
        this.historyLoading = false;
      }
    },
    async saveRemoteLog(role, content, refs = [], time = null, metrics = {}) {
      if (!content && role === 'user') return;
      if (!this.currentChatDbId) return; // Must have a session DB ID

      let contentToSave = content;
      if (role === 'ai') {
        contentToSave = JSON.stringify({
          text: content,
          references: refs,
          thoughts: metrics.thoughts || ''
        });
      }

      try {
        await saveChatHistory({
          user_copilot_chat_id: this.currentChatDbId,
          session_id: this.sessionId,
          user_id: getLocalStorage('user_id'),
          role_id: getLocalStorage('role_id') || '',
          chat_role: role,
          content: contentToSave || '',
          chat_time: time || new Date().toISOString(),
          think_seconds: metrics.thinkSeconds,
          total_seconds: metrics.totalSeconds,
          thoughts: metrics.thoughts
        });
      } catch (e) {
        console.error('Failed to save chat log', e);
      }
    },
    formatMessage,
    shouldClampUserMessage(msg) {
      if (!msg || msg.type !== 'user' || !msg.text) return false;

      const explicitLineCount = String(msg.text).split(/\r?\n/).length;
      if (explicitLineCount > 5) return true;

      const normalizedText = String(msg.text).replace(/\s+/g, '');
      let visualLength = 0;
      for (const char of normalizedText) {
        visualLength += /[\u4e00-\u9fff\u3000-\u303f\uff00-\uffef]/.test(char) ? 1 : 0.55;
      }
      return visualLength > 95;
    },
    isUserMessageExpanded(index) {
      return Boolean(this.expandedUserMessages[index]);
    },
    isUserMessageCollapsed(msg, index) {
      return this.shouldClampUserMessage(msg) && !this.isUserMessageExpanded(index);
    },
    getUserMessageToggleText(index) {
      return this.isUserMessageExpanded(index) ? this.$t('ai_show_less') : this.$t('ai_show_more');
    },
    toggleUserMessage(index) {
      if (this.isUserMessageExpanded(index)) {
        this.$delete(this.expandedUserMessages, index);
      } else {
        this.$set(this.expandedUserMessages, index, true);
      }
    },
    hasThoughtText(msg) {
      return Boolean(msg && msg.type === 'ai' && String(msg.thoughtText || '').trim());
    },
    isThoughtMessageExpanded(index) {
      return Boolean(this.expandedThoughtMessages[index]);
    },
    toggleThoughtMessage(index) {
      if (this.isThoughtMessageExpanded(index)) {
        this.$delete(this.expandedThoughtMessages, index);
      } else {
        this.$set(this.expandedThoughtMessages, index, true);
      }
    },
    getStreamingTextValue(value) {
      if (!value) return '';
      if (typeof value === 'string') return value;
      if (Array.isArray(value)) {
        return value
          .map(item => this.getStreamingTextValue(item))
          .filter(Boolean)
          .join('\n');
      }
      if (typeof value === 'object') {
        return this.getStreamingTextValue(
          value.text ||
          value.content ||
          value.thought ||
          value.response ||
          value.reasoning_content ||
          value.reasoningContent ||
          value.reasoning_text ||
          value.reasoning ||
          ''
        );
      }
      return String(value);
    },
    getThoughtTextFromStream(json) {
      const output = json && json.output ? json.output : {};
      const choices = output.choices || json.choices || [];
      const firstChoice = choices && choices.length ? choices[0] : null;
      const message = firstChoice && firstChoice.message ? firstChoice.message : {};

      return this.getStreamingTextValue(
        output.thoughts ||
        output.thought ||
        output.reasoning_content ||
        output.reasoningContent ||
        output.reasoning_text ||
        output.reasoning ||
        message.reasoning_content ||
        message.reasoningContent ||
        json.thoughts ||
        json.thought ||
        json.reasoning_content ||
        json.reasoningContent
      );
    },
    applyStreamingText(aiMessageIndex, fieldName, newText) {
      if (!newText) return false;

      const aiMsg = this.chatHistory[aiMessageIndex];
      if (!aiMsg) return false;

      const normalizedText = String(newText);
      const currentContent = aiMsg[fieldName] || '';
      if (currentContent && normalizedText.startsWith(currentContent)) {
        aiMsg[fieldName] = normalizedText;
      } else {
        aiMsg[fieldName] = currentContent + normalizedText;
      }
      return true;
    },
    updateThoughtSeconds(aiMessageIndex) {
      const aiMsg = this.chatHistory[aiMessageIndex];
      if (!aiMsg || aiMsg.thoughtSeconds || !aiMsg.thoughtStartedAt) return;

      const elapsed = Math.max(1, Math.round((Date.now() - aiMsg.thoughtStartedAt) / 1000));
      aiMsg.thoughtSeconds = elapsed;
    },
    updateTotalSeconds(aiMessageIndex) {
      const aiMsg = this.chatHistory[aiMessageIndex];
      if (!aiMsg || aiMsg.totalSeconds || !aiMsg.totalStartedAt) return;

      const elapsed = Math.max(1, Math.round((Date.now() - aiMsg.totalStartedAt) / 1000));
      aiMsg.totalSeconds = elapsed;
    },
    async handleAsk() {
      const text = this.question.trim();
      if (!text) return;

      const requestTime = new Date().toISOString();

      // Add user message
      this.chatHistory.push({ type: 'user', text });
      this.question = '';
      this.showThinkingModeMenu = false;
      this.isLoading = true;
      this.startThinkingAnimation();

      // Add placeholder for AI response
      const aiMessageIndex = this.chatHistory.push({
        type: 'ai',
        text: '',
        thoughtText: '',
        loading: true,
        references: [],
        thoughtStartedAt: Date.now(),
        thoughtSeconds: null,
        totalStartedAt: Date.now(),
        totalSeconds: null
      }) - 1;

      this.$nextTick(() => {
        const body = this.$refs.chatBody;
        if (body) body.scrollTop = body.scrollHeight;
      });

      // If session exists, save user log immediately to keep accurate time
      let userMessageSaved = false;
      if (this.currentChatDbId) {
        this.saveRemoteLog('user', text, [], requestTime);
        userMessageSaved = true;
      }

      let buffer = '';
      let aiResponseTime = null;

      try {
        await streamSuperRepCopilot({
          content: text,
          sessionId: this.sessionId,
          agentName: this.currentCopilotAgentName || this.getDefaultCopilotAgentName(),
          enableThinking: this.thinkingEnabled,
          hasThoughts: this.thinkingEnabled,
          onChunk: (chunk) => {
            buffer += chunk;
            const lines = buffer.split('\n');
            buffer = lines.pop();

            for (const line of lines) {
              const trimmedLine = line.trim();
              if (!trimmedLine.startsWith('data:')) continue;

              const jsonStr = trimmedLine.substring(5).trim();
              if (!jsonStr) continue;

              try {
                if (!aiResponseTime) {
                  aiResponseTime = new Date().toISOString();
                }

                const json = JSON.parse(jsonStr);

                if (json.output && json.output.session_id) {
                  this.sessionId = json.output.session_id;
                } else if (json.session_id) {
                  this.sessionId = json.session_id;
                }

                if (json.output && json.output.doc_references) {
                  this.chatHistory[aiMessageIndex].references = json.output.doc_references;
                }

                const thoughtText = this.getThoughtTextFromStream(json);
                this.applyStreamingText(aiMessageIndex, 'thoughtText', thoughtText);

                if (json.output && json.output.text) {
                  const newText = json.output.text;

                  if (!this.chatHistory[aiMessageIndex].text && newText) {
                    this.stopThinkingAnimation();
                    this.updateThoughtSeconds(aiMessageIndex);
                  }

                  this.applyStreamingText(aiMessageIndex, 'text', newText);
                  // this.chatHistory[aiMessageIndex].loading = false;

                  this.$nextTick(() => {
                    const body = this.$refs.chatBody;
                    if (body) body.scrollTop = body.scrollHeight;
                  });
                }
              } catch (e) {
                console.error('Error parsing SSE JSON:', e);
              }
            }
          },
          onComplete: async () => {
            this.stopThinkingAnimation();
            this.updateThoughtSeconds(aiMessageIndex);
            this.updateTotalSeconds(aiMessageIndex);
            this.chatHistory[aiMessageIndex].loading = false;
            this.isLoading = false;

            // Create Session if not exist (Only if we haven't created a DB session yet)
            if (!this.isSessionCreated) {
              try {
                const res = await saveChatSession({
                  session_id: this.sessionId,
                  user_id: getLocalStorage('user_id'),
                  role_id: getLocalStorage('role_id') || '',
                  chat_title: text.length > 50 ? text.substring(0, 50) + '...' : text,
                  chat_time: requestTime
                });

                let newId = null;
                if (res && res.entity && res.entity.user_copilot_chat_id) {
                  newId = res.entity.user_copilot_chat_id;
                } else if (res && res.user_copilot_chat_id) {
                  newId = res.user_copilot_chat_id;
                }

                if (newId) {
                  this.currentChatDbId = newId;
                  this.isSessionCreated = true;
                }
              } catch (e) {
                console.error('Failed to create chat session', e);
              }
            }

            // Record logs if we have a DB ID
            if (this.currentChatDbId) {
              // If we haven't saved user message yet (because session was just created), save it now with original time
              if (!userMessageSaved) {
                this.saveRemoteLog('user', text, [], requestTime);
              }
              const aiMsg = this.chatHistory[aiMessageIndex];
              this.saveRemoteLog('ai', aiMsg.text, aiMsg.references, aiResponseTime || new Date().toISOString(), {
                thinkSeconds: aiMsg.thoughtSeconds,
                totalSeconds: aiMsg.totalSeconds,
                thoughts: aiMsg.thoughtText
              });
            }
          },
          onError: (err) => {
            console.error(err);
            this.stopThinkingAnimation();
            this.updateThoughtSeconds(aiMessageIndex);
            this.updateTotalSeconds(aiMessageIndex);
            this.chatHistory[aiMessageIndex].text += '\n[Error: ' + err.message + ']';
            this.chatHistory[aiMessageIndex].loading = false;
            this.isLoading = false;
          }
        });
      } catch (e) {
        console.error(e);
        this.stopThinkingAnimation();
        this.updateThoughtSeconds(aiMessageIndex);
        this.updateTotalSeconds(aiMessageIndex);
        this.chatHistory[aiMessageIndex].text += '\n[Error: Request failed]';
        this.chatHistory[aiMessageIndex].loading = false;
        this.isLoading = false;
      }
    }
  }
};
</script>

<style lang="scss" scoped>
.ai-chat-container {
  position: fixed;
  width: 130px;
  z-index: 9999;
  touch-action: none; /* Prevent scrolling while dragging */

  &.is-expanded {
    touch-action: auto;
  }
}

.ai-chat-bar {
  background: rgba(255, 255, 255, 0.95);
  border-radius: 30px;
  box-shadow: 0 4px 12px rgba(0, 0, 0, 0.25);
  padding: 5px 5px;
  display: flex;
  align-items: center;
  cursor: pointer;
  transition: all 0.3s ease;
  border: 1px solid #eee;

  &:active {
    transform: scale(0.98);
  }
}

.ai-icon-wrapper {
  width: 44px;
  height: 44px;
  background: linear-gradient(135deg, #2fbf9b 0%, #008f72 100%);
  border-radius: 50%;
  display: flex;
  align-items: center;
  justify-content: center;
  margin-right: 10px;
}

.ai-icon {
  color: white;
  font-size: 22px;
}

.ai-input-placeholder {
  color: #999;
  font-size: 14px;
  flex: 1;
  height: 20px;
  padding-top: 5px;
  overflow: hidden;
  position: relative;
}

.placeholder-item {
  width: 100%;
  white-space: nowrap;
  overflow: hidden;
  text-overflow: ellipsis;
}

.slide-fade-enter-active, .slide-fade-leave-active {
  transition: all 0.5s ease;
}
.slide-fade-enter {
  transform: translateY(10px);
  opacity: 0;
}
.slide-fade-leave-to {
  transform: translateY(-10px);
  opacity: 0;
}

.chat-window {
  --chat-header-height: 48px;
  display: flex;
  flex-direction: column;
  height: 100%;
  position: relative;
  background: #fbfaff;
}

.chat-header {
  position: relative;
  min-height: var(--chat-header-height);
  box-sizing: border-box;
  padding: 10px 15px;
  background:
    radial-gradient(circle at 18% 0%, rgba(255, 255, 255, 0.28) 0, rgba(255, 255, 255, 0) 34%),
    linear-gradient(135deg, #1f9f83 0%, #00a77a 58%, #007a5b 100%);
  display: flex;
  justify-content: space-between;
  align-items: center;
  border-bottom: none;
  border-radius: 12px 12px 0 0;
  box-shadow: 0 8px 22px rgba(85, 70, 145, 0.18);
  overflow: hidden;

  &::after {
    content: '';
    position: absolute;
    left: 16px;
    right: 16px;
    bottom: 0;
    height: 1px;
    background: rgba(255, 255, 255, 0.24);
  }

  .title-left {
    display: flex;
    align-items: center;
    position: relative;
    z-index: 1;

    .header-icon {
      width: 28px;
      height: 28px;
      box-sizing: border-box;
      padding: 6px;
      color: #fff;
      margin-right: 9px;
      font-size: 16px;
    }

    .title {
      font-weight: bold;
      font-size: 16px;
      color: white;
      letter-spacing: 0;
    }
  }
}

.close-icon {
  font-size: 20px;
  color: #fff;
  padding: 6px;
  opacity: 0.9;
  border-radius: 50%;
  background: rgba(255, 255, 255, 0.10);
}

.header-actions {
  display: flex;
  align-items: center;
  position: relative;
  z-index: 1;

  .history-icon {
    font-size: 20px;
    color: #fff;
    padding: 6px;
    margin-right: 8px;
    opacity: 0.9;
    border-radius: 50%;
    background: rgba(255, 255, 255, 0.10);
  }
}

.history-overlay {
  position: absolute;
  top: var(--chat-header-height);
  left: 0;
  right: 0;
  bottom: 0;
  background: white;
  z-index: 100;
  display: flex;
  flex-direction: column;

  .history-header {
    padding: 15px;
    font-weight: bold;
    font-size: 16px;
    display: flex;
    justify-content: space-between;
    border-bottom: 1px solid #eee;
  }

  .history-content {
    flex: 1;
    overflow-y: auto;
    padding: 10px;
    -webkit-overflow-scrolling: touch; /* Smooth scrolling on iOS */
  }

  .history-item {
    display: flex;
    justify-content: space-between;
    align-items: center;
    padding: 15px;
    border-bottom: 1px solid #f5f5f5;
    background: #fff;

    &:active {
      background: #f9f9f9;
    }

    .history-info {
      flex: 1;
      overflow: hidden;
      margin-right: 10px;
    }

    .history-title {
      font-size: 14px;
      color: #333;
      margin-bottom: 5px;
      font-weight: 500;
      white-space: nowrap;
      overflow: hidden;
      text-overflow: ellipsis;
    }

    .history-time {
      font-size: 12px;
      color: #999;
    }

    .delete-icon {
      color: #ccc;
      font-size: 20px;
      padding: 5px;

      &:active {
        color: #ff4d4f;
      }
    }
  }

  .no-history, .no-more, .loading-more {
    padding: 15px;
    text-align: center;
    color: #999;
    font-size: 14px;
  }

  .dot-loading {
    display: inline-flex;
    align-items: center;
    height: 20px;

    .dot {
      width: 6px;
      height: 6px;
      margin: 0 2px;
      background-color: #999;
      border-radius: 50%;
      animation: typing 1.4s infinite ease-in-out both;

      &:nth-child(1) {
        animation-delay: -0.32s;
      }

      &:nth-child(2) {
        animation-delay: -0.16s;
      }
    }
  }
}

.chat-body {
  flex: 1;
  overflow-y: auto;
  padding: 20px 20px 104px;
  display: flex;
  flex-direction: column;
  background: #fbfaff;
  -webkit-overflow-scrolling: touch;

  &.is-empty {
    background: linear-gradient(135deg, #e7f6ed 0%, #f0fff6 50%, #ffffff 70%);
  }
}

.empty-state {
  margin-top: 10px;
  text-align: left;

  .welcome-text {
    color: #333;
    margin-bottom: 20px;
    font-size: 14px;
  }
}

.section-title {
  font-size: 12px;
  font-weight: bold;
  color: #333;
  margin-bottom: 10px;
  text-align: left;
  padding-left: 5px;
//   border-left: 3px solid #00b852;
  line-height: 1.2;
}

.workflow-guide {
  font-size: 14px;
  color: #666;
  text-align: left;
  background: rgba(255, 255, 255, 0.6);
  padding: 12px;
  border-radius: 8px;
  line-height: 1.25;

  .guide-title {
    font-weight: 500;
    margin-bottom: 10px;
    color: #00b852;
  }

  b {
    margin: 0 2px;
    color: #333;
    font-weight: 900;
  }
}

.quick-actions {
  display: flex;
  flex-wrap: wrap;
  gap: 10px;
  justify-content: center;
  background: rgba(255, 255, 255, 0.6);
  padding: 12px;
  border-radius: 8px;

  .action-chip {
    border: 1px solid #999;
    color: #555;
    padding: 6px 14px;
    border-radius: 20px;
    font-size: 12px;
    background: #fff;
    cursor: pointer;
  }
}

.message-list {
  display: flex;
  flex-direction: column;
  gap: 15px;
}

.history-loading {
  flex: 1;
  display: flex;
  align-items: center;
  justify-content: center;
  color: #666;
}

.message-item {
  display: flex;
  max-width: 100%;

  &.user {
    align-self: flex-end;
    max-width: 80%;

    .bubble {
      background:
        radial-gradient(circle at 26% 20%, rgba(255, 255, 255, 0.08) 0, rgba(255, 255, 255, 0) 46%),
        linear-gradient(135deg, #008f40 0%, #00b852 62%, #007a36 100%);
      color: white;
      border-radius: 12px 0 12px 12px;

      ::v-deep h1,
      ::v-deep h2,
      ::v-deep h3,
      ::v-deep h4,
      ::v-deep div,
      ::v-deep span,
      ::v-deep strong,
      ::v-deep blockquote {
        color: rgba(255, 255, 255, 0.96) !important;
      }

      ::v-deep hr {
        border-top-color: rgba(255, 255, 255, 0.28) !important;
      }
    }
  }

  &.ai {
    align-self: flex-start;
    flex-direction: column;
    width: 100%;
    .bubble {
      width: 100%;
      box-sizing: border-box;
      padding: 6px 0 8px;
      background: transparent;
      color: #333;
      border-radius: 0;
      box-shadow: none;
    }
  }

  .bubble {
    box-sizing: border-box;
    padding: 10px 15px;
    font-size: 14px;
    line-height: 1.5;
    overflow-wrap: break-word;
  }

  .message-text {
    white-space: normal;

    &.is-clamped {
      display: -webkit-box;
      -webkit-line-clamp: 5;
      -webkit-box-orient: vertical;
      overflow: hidden;
    }
  }

  &.user .message-text.is-clamped {
    -webkit-mask-image: linear-gradient(to bottom, #000 68%, rgba(0, 0, 0, 0.72) 82%, transparent 100%);
    mask-image: linear-gradient(to bottom, #000 68%, rgba(0, 0, 0, 0.72) 82%, transparent 100%);
  }

  .message-toggle {
    display: block;
    margin: 6px auto 0 0;
    padding: 0;
    border: 0;
    background: transparent;
    color: rgba(255, 255, 255, 0.86);
    font-size: 12px;
    line-height: 1.4;
    cursor: pointer;
  }

  .thought-time {
    margin-bottom: 6px;
    color: #999;
    font-size: 12px;
    line-height: 1.2;

    &.is-clickable {
      cursor: pointer;
    }
  }

  .thinking-text.is-clickable {
    cursor: pointer;
  }

  .thought-content {
    margin: 0 0 10px;
    padding: 0;
    color: #9aa1ad;
    font-size: 12px;
    line-height: 1.55;
    white-space: normal;

    ::v-deep p,
    ::v-deep ul,
    ::v-deep ol,
    ::v-deep blockquote {
      margin-top: 0;
      margin-bottom: 6px;
    }

    ::v-deep * {
      color: inherit !important;
      border-color: rgba(154, 161, 173, 0.24) !important;
    }
  }
}

.chat-footer {
  position: absolute;
  left: 0;
  right: 0;
  bottom: 0;
  z-index: 10;
  padding: 10px 15px;
  background: transparent;
}

.thinking-dropdown {
  position: relative;
  flex-shrink: 0;
  margin: 0 3px 0 2px;
}

.thinking-mode-trigger {
  display: inline-flex;
  align-items: center;
  justify-content: center;
  height: 34px;
  min-width: 48px;
  padding: 0 4px 8px;
  border: 0;
  background: transparent;
  color: #777;
  font-size: 13px;
  line-height: 1;
  white-space: nowrap;
  cursor: pointer;

  &:disabled {
    color: #bbb;
    cursor: not-allowed;
  }

  &.open .thinking-mode-arrow {
    transform: rotate(180deg);
  }
}

.thinking-mode-arrow {
  margin-left: 3px;
  font-size: 12px;
  transition: transform 0.18s ease;
}

.thinking-mode-menu {
  position: absolute;
  right: 6px;
  bottom: calc(100% + 14px);
  min-width: 96px;
  padding: 6px;
  background: #fff;
  border: 1px solid #eee;
  border-radius: 8px;
  box-shadow: 0 8px 22px rgba(0, 0, 0, 0.12);
  z-index: 30;
}

.thinking-mode-option {
  width: 100%;
  height: 32px;
  display: flex;
  align-items: center;
  justify-content: space-between;
  padding: 0 8px;
  border: 0;
  border-radius: 6px;
  background: transparent;
  color: #555;
  font-size: 13px;
  text-align: left;
  cursor: pointer;

  &.active {
    color: #00b852;
    background: #fff5f2;
  }

  .van-icon {
    margin-left: 8px;
    font-size: 13px;
  }
}

.input-row {
  width: calc(100% - 20px);
  display: flex;
  align-items: center;
  background: #fff;
  border-radius: 48px;
  padding: 8px 8px 8px 16px;
  box-shadow: 0 2px 8px rgba(0, 0, 0, 0.08);
  border: 1px solid #f0f0f0;
  transition: all 0.3s;

  &.focused {
    border-color: #00b852;
    box-shadow: 0 2px 12px rgba(0, 184, 82, 0.15);
  }
}

.input-wrapper {
  flex: 1;
  display: flex;
  flex-direction: column;
  justify-content: center;
  overflow: hidden;
  margin-right: 8px;

  .text-input {
    width: 100%;
    padding: 0;
    background: transparent;
    font-size: 15px;

    ::v-deep .van-field__control {
      color: #333;
      max-height: 80px;
      &::placeholder {
        color: #ccc;
      }
    }
  }
}

.action-btn {
  margin-left: 0;
  display: flex;
  align-items: center;
  justify-content: center;
}

.mic-btn-integrated {
  width: 50px;
  height: 50px;
  background: linear-gradient(135deg, #00b852 0%, #17c86b 100%);
  border-radius: 50%;
  display: flex;
  align-items: center;
  justify-content: center;
  box-shadow: 0 2px 6px rgba(0, 184, 82, 0.3);
  cursor: pointer;
  transition: transform 0.1s;

  &:active {
    transform: scale(0.95);
  }
}

.speech-wrapper {
  display: flex;
  align-items: center;
  justify-content: center;
}

.mic-btn {
  width: 40px;
  height: 40px;
  background: #00b852;
  border-radius: 50%;
  display: flex;
  align-items: center;
  justify-content: center;
  box-shadow: 0 2px 6px rgba(0, 184, 82, 0.3);
  cursor: pointer;
  transition: transform 0.1s;

  &:active {
    transform: scale(0.95);
  }
}

@keyframes pulse {
  0% { transform: translateY(-50%) scale(1); opacity: 1; }
  50% { transform: translateY(-50%) scale(1.2); opacity: 0.7; }
  100% { transform: translateY(-50%) scale(1); opacity: 1; }
}

::v-deep .ref-tag {
  color: #00b852;
  font-weight: bold;
  margin: 0 2px;
  font-size: 0.9em;
  vertical-align: super;
}

.typing-indicator {
  display: inline-flex;
  align-items: center;
  margin-left: 4px;
  vertical-align: middle;
  height: 20px;

  .thinking-text {
    margin-right: 6px;
    font-size: 13px;
    color: #666;
  }

  .dot {
    width: 6px;
    height: 6px;
    margin: 0 2px;
    background-color: #999;
    border-radius: 50%;
    animation: typing 1.4s infinite ease-in-out both;

    &:nth-child(1) {
      animation-delay: -0.32s;
    }

    &:nth-child(2) {
      animation-delay: -0.16s;
    }
  }
}

.references-list {
  margin-top: 12px;
  padding-top: 12px;
  border-top: 1px solid #eee;
  font-size: 12px;

  .ref-header {
    font-weight: bold;
    color: #666;
    margin-bottom: 6px;
  }

  .ref-item {
    display: flex;
    margin-bottom: 4px;
    color: #00b852;

    .ref-index {
      margin-right: 4px;
      flex-shrink: 0;
    }

    .ref-content {
      word-break: break-all;
    }
  }
}

@keyframes typing {
  0%, 80%, 100% {
    transform: scale(0);
  }
  40% {
    transform: scale(1);
  }
}

::v-deep .visit-link-btn {
  display: inline-flex;
  align-items: center;
  color: #00b852;
  font-weight: bold;
  cursor: pointer;
  background: rgba(0, 184, 82, 0.1);
  padding: 4px 10px;
  border-radius: 15px;
  border: 1px solid rgba(0, 184, 82, 0.2);
  margin: 4px 0;
  font-size: 13px;

  &:active {
    background: rgba(0, 184, 82, 0.2);
  }
}
</style>
